import { useState } from "react";
import { FlagIcon, FlagBannerIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import api, { formatApiError } from "../../lib/api";

/**
 * Phase 5e — Internal "needs follow-up" flag toggle.
 *
 * Owner/ops only. Optimistic flip + revert on error. No confirm dialog.
 * Re-flagging an already-flagged report is intentional (server bumps
 * `flagged_at` to now).
 */
export default function FlagToggleButton({
  reportId,
  flagged,
  flaggedAt,
  flaggedByName,
  size = 16,
  testid = "flag-toggle",
  onChange,
}) {
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState(null);

  const isFlagged = optimistic === null ? !!flagged : optimistic;

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    const next = !isFlagged;
    setOptimistic(next);
    setBusy(true);
    try {
      const r = await api.patch(`/diagnostic-reports/${reportId}/flag`, {
        flagged: next,
      });
      setOptimistic(null);
      onChange?.(r.data);
      toast.success(next ? "Flagged for follow-up" : "Flag cleared");
    } catch (err) {
      setOptimistic(null);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const tooltip = isFlagged
    ? `Flagged for follow-up${flaggedByName ? ` by ${flaggedByName}` : ""}${
        flaggedAt ? ` · ${_relative(flaggedAt)}` : ""
      }`
    : "Flag for follow-up";

  const Icon = isFlagged ? FlagBannerIcon : FlagIcon;
  const cls = isFlagged
    ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
    : "border-slate-200 hover:border-slate-400 bg-white";
  const iconCls = isFlagged ? "text-amber-600" : "text-slate-500";

  return (
    <button
      type="button"
      data-testid={testid}
      data-flagged={isFlagged ? "true" : "false"}
      aria-label={isFlagged ? "Unflag" : "Flag for follow-up"}
      title={tooltip}
      onClick={toggle}
      disabled={busy}
      className={`rounded-md border p-1.5 transition disabled:opacity-50 ${cls}`}
    >
      <Icon size={size} weight={isFlagged ? "fill" : "regular"} className={iconCls} />
    </button>
  );
}

function _relative(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
